import React, { useState, useEffect } from 'react';

const LanguagesTabWithUI = ({ isDarkMode, API, adminToken, showNotification }) => {
  const [subTab, setSubTab] = useState('ui');
  const [uiLanguages, setUiLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLang, setNewLang] = useState({ code: '', name: '', flag: '', is_rtl: false });
  const [saving, setSaving] = useState(false);

  // Help & Support subtab state
  const [helpLanguages, setHelpLanguages] = useState([]);
  const [selectedHelpLang, setSelectedHelpLang] = useState(null);
  const [editingHelpKey, setEditingHelpKey] = useState(null);
  const [editHelpValue, setEditHelpValue] = useState('');
  const [searchHelpKey, setSearchHelpKey] = useState('');
  const [showHelpAddModal, setShowHelpAddModal] = useState(false);
  const [newHelpLang, setNewHelpLang] = useState({ code: '', name: '' });
  const [savingHelp, setSavingHelp] = useState(false);

  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const loadUILanguages = async () => {
    try {
      const res = await fetch(`${API}/admin/ui-translations`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (res.ok) {
        const data = await res.json();
        setUiLanguages(data);
        if (data.length > 0 && !selectedLang) setSelectedLang(data[0].lang_code);
      }
    } catch (err) {
      showNotification('Failed to load UI languages', 'error');
    }
  };

  const loadHelpContent = async () => {
    try {
      const res = await fetch(`${API}/admin/help-content`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (res.ok) {
        const data = await res.json();
        setHelpLanguages(data);
        if (data.length > 0 && !selectedHelpLang) setSelectedHelpLang(data.find(l => l.lang_code === 'en')?.lang_code || data[0].lang_code);
      }
    } catch (err) {
      showNotification('Failed to load help content', 'error');
    }
  };

  useEffect(() => { loadUILanguages(); loadHelpContent(); }, []);

  const handleSaveKey = async (langCode, key, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/ui-translations/${langCode}`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ key, value })
      });
      if (res.ok) {
        showNotification('Translation saved', 'success');
        setUiLanguages(prev => prev.map(l => l.lang_code === langCode
          ? { ...l, translations: { ...l.translations, [key]: value } }
          : l
        ));
        setEditingKey(null);
      }
    } catch (err) {
      showNotification('Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleToggleUI = async (langCode) => {
    try {
      const res = await fetch(`${API}/admin/ui-translations/${langCode}/toggle`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(data.message, 'success');
        setUiLanguages(prev => prev.map(l => l.lang_code === langCode ? { ...l, is_active: data.is_active } : l));
      }
    } catch (err) {
      showNotification('Failed to toggle', 'error');
    }
  };

  const handleDeleteUI = async (langCode) => {
    if (!window.confirm(`Delete "${langCode}" UI language? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/ui-translations/${langCode}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Language deleted', 'success');
        setUiLanguages(prev => prev.filter(l => l.lang_code !== langCode));
        if (selectedLang === langCode) setSelectedLang(uiLanguages[0]?.lang_code || null);
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to delete', 'error');
      }
    } catch (err) {
      showNotification('Failed to delete', 'error');
    }
  };

  const handleAddLanguage = async () => {
    if (!newLang.code || !newLang.name) { showNotification('Code and name required', 'error'); return; }
    try {
      const res = await fetch(`${API}/admin/ui-translations`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ lang_code: newLang.code, lang_name: newLang.name, flag: newLang.flag || newLang.code.toUpperCase(), is_rtl: newLang.is_rtl })
      });
      if (res.ok) {
        showNotification('Language added (English keys copied as template)', 'success');
        setShowAddModal(false);
        setNewLang({ code: '', name: '', flag: '', is_rtl: false });
        loadUILanguages();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to add', 'error');
      }
    } catch (err) {
      showNotification('Failed to add language', 'error');
    }
  };

  // Help & Support handlers
  const handleSaveHelpKey = async (langCode, key, value) => {
    setSavingHelp(true);
    try {
      const res = await fetch(`${API}/admin/help-content/${langCode}`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ key, value })
      });
      if (res.ok) {
        showNotification('Content saved', 'success');
        setHelpLanguages(prev => prev.map(l => l.lang_code === langCode
          ? { ...l, content: { ...l.content, [key]: value } }
          : l
        ));
        setEditingHelpKey(null);
      }
    } catch (err) {
      showNotification('Failed to save', 'error');
    }
    setSavingHelp(false);
  };

  const handleToggleHelp = async (langCode) => {
    try {
      const res = await fetch(`${API}/admin/help-content/${langCode}/toggle`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(data.message, 'success');
        setHelpLanguages(prev => prev.map(l => l.lang_code === langCode ? { ...l, is_active: data.is_active } : l));
      }
    } catch (err) {
      showNotification('Failed to toggle', 'error');
    }
  };

  const handleDeleteHelp = async (langCode) => {
    if (!window.confirm(`Delete "${langCode}" Help content? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/help-content/${langCode}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Language deleted', 'success');
        setHelpLanguages(prev => prev.filter(l => l.lang_code !== langCode));
        if (selectedHelpLang === langCode) setSelectedHelpLang(helpLanguages[0]?.lang_code || null);
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to delete', 'error');
      }
    } catch (err) {
      showNotification('Failed to delete', 'error');
    }
  };

  const handleAddHelpLanguage = async () => {
    if (!newHelpLang.code || !newHelpLang.name) { showNotification('Code and name required', 'error'); return; }
    try {
      const res = await fetch(`${API}/admin/help-content`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ lang_code: newHelpLang.code, lang_name: newHelpLang.name })
      });
      if (res.ok) {
        showNotification('Language added (English content copied as template)', 'success');
        setShowHelpAddModal(false);
        setNewHelpLang({ code: '', name: '' });
        loadHelpContent();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to add', 'error');
      }
    } catch (err) {
      showNotification('Failed to add language', 'error');
    }
  };

  const currentLang = uiLanguages.find(l => l.lang_code === selectedLang);
  const enLang = uiLanguages.find(l => l.lang_code === 'en');
  const allKeys = currentLang ? Object.keys(currentLang.translations).sort() : [];
  const filteredKeys = searchKey
    ? allKeys.filter(k => k.toLowerCase().includes(searchKey.toLowerCase()) || (currentLang.translations[k] || '').toLowerCase().includes(searchKey.toLowerCase()))
    : allKeys;

  // Help & Support computed values
  const currentHelpLang = helpLanguages.find(l => l.lang_code === selectedHelpLang);
  const helpEnLang = helpLanguages.find(l => l.lang_code === 'en');
  const allHelpKeys = currentHelpLang ? Object.keys(currentHelpLang.content || {}).sort() : [];
  const filteredHelpKeys = searchHelpKey
    ? allHelpKeys.filter(k => k.toLowerCase().includes(searchHelpKey.toLowerCase()) || (currentHelpLang.content[k] || '').toLowerCase().includes(searchHelpKey.toLowerCase()))
    : allHelpKeys;

  const subTabBtn = (id, label) => (
    <button key={id} onClick={() => setSubTab(id)} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
      subTab === id
        ? `border-blue-500 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`
        : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
    }`}>{label}</button>
  );

  return (
    <div className="space-y-6">
      <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Language Management</h3>

      {/* Subtabs */}
      <div className={`flex gap-1 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {subTabBtn('ui', 'UI Languages')}
        {subTabBtn('help', 'Help & Support')}
      </div>

      {/* UI Languages */}
      {subTab === 'ui' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage UI text shown to users: buttons, menus, labels, error messages.
            </p>
            <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">+ Add Language</button>
          </div>

          {/* Add Language Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className={`p-6 rounded-xl w-96 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Add New UI Language</h4>
                <div className="space-y-3">
                  <input type="text" placeholder="Code (e.g. de, pt, tr)" value={newLang.code} onChange={(e) => setNewLang({...newLang, code: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} />
                  <input type="text" placeholder="Name (e.g. Deutsch)" value={newLang.name} onChange={(e) => setNewLang({...newLang, name: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} />
                  <input type="text" placeholder="Label (e.g. DE)" value={newLang.flag} onChange={(e) => setNewLang({...newLang, flag: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} />
                  <label className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={newLang.is_rtl} onChange={(e) => setNewLang({...newLang, is_rtl: e.target.checked})} />
                    Right-to-left (RTL) language
                  </label>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>All English keys will be copied as a starting template for you to translate.</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={handleAddLanguage} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Add</button>
                  <button onClick={() => { setShowAddModal(false); setNewLang({ code: '', name: '', flag: '', is_rtl: false }); }} className={`flex-1 px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300'}`}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Language buttons */}
          <div className="flex gap-2 flex-wrap">
            {uiLanguages.map((lang) => (
              <button key={lang.lang_code} onClick={() => setSelectedLang(lang.lang_code)} className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                selectedLang === lang.lang_code
                  ? 'bg-blue-600 text-white border-blue-600'
                  : isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 hover:bg-gray-50'
              } ${!lang.is_active ? 'opacity-50' : ''}`}>
                {lang.flag || lang.lang_code.toUpperCase()} — {lang.lang_name}
                {!lang.is_active && ' (off)'}
              </button>
            ))}
          </div>

          {/* Language actions */}
          {currentLang && (
            <div className="flex gap-2 items-center flex-wrap">
              <button onClick={() => handleToggleUI(currentLang.lang_code)} className={`px-3 py-1.5 rounded text-sm text-white ${currentLang.is_active ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}>
                {currentLang.is_active ? 'Deactivate' : 'Activate'}
              </button>
              {currentLang.lang_code !== 'en' && (
                <button onClick={() => handleDeleteUI(currentLang.lang_code)} className="px-3 py-1.5 rounded text-sm bg-red-600 text-white hover:bg-red-700">Delete</button>
              )}
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{allKeys.length} keys</span>
            </div>
          )}

          {/* Search */}
          <input type="text" placeholder="Search keys or values..." value={searchKey} onChange={(e) => setSearchKey(e.target.value)} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'border-gray-300 placeholder-gray-400'}`} />

          {/* Translation table */}
          {currentLang && (
            <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Key</th>
                      {selectedLang !== 'en' && enLang && <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English</th>}
                      <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{currentLang.lang_name}</th>
                      <th className={`px-3 py-2 text-left font-medium w-20 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKeys.map((key) => (
                      <tr key={key} className={`border-t ${isDarkMode ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`px-3 py-2 font-mono text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{key}</td>
                        {selectedLang !== 'en' && enLang && <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{enLang.translations[key] || '—'}</td>}
                        <td className="px-3 py-2">
                          {editingKey === key ? (
                            <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus
                              className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveKey(currentLang.lang_code, key, editValue); if (e.key === 'Escape') setEditingKey(null); }}
                            />
                          ) : (
                            <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>{currentLang.translations[key] || ''}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {editingKey === key ? (
                            <div className="flex gap-1">
                              <button onClick={() => handleSaveKey(currentLang.lang_code, key, editValue)} disabled={saving} className="text-green-600 hover:text-green-700 text-xs font-medium">Save</button>
                              <button onClick={() => setEditingKey(null)} className="text-gray-500 hover:text-gray-700 text-xs">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => { setEditingKey(key); setEditValue(currentLang.translations[key] || ''); }} className="text-blue-600 hover:text-blue-700 text-xs font-medium">Edit</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uiLanguages.length === 0 && (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="text-lg mb-2">No UI languages found</p>
              <p className="text-sm">Run the seed script first: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">python seed_ui_translations.py</code></p>
            </div>
          )}
        </div>
      )}

      {/* Help & Support Content */}
      {subTab === 'help' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage Help & Support and About Us page translations.
            </p>
            <button onClick={() => setShowHelpAddModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">+ Add Language</button>
          </div>

          {/* Add Help Language Modal */}
          {showHelpAddModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className={`p-6 rounded-xl w-96 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Add New Help Language</h4>
                <div className="space-y-3">
                  <input type="text" placeholder="Code (e.g. de, pt, tr)" value={newHelpLang.code} onChange={(e) => setNewHelpLang({...newHelpLang, code: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} />
                  <input type="text" placeholder="Name (e.g. Deutsch)" value={newHelpLang.name} onChange={(e) => setNewHelpLang({...newHelpLang, name: e.target.value})} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} />
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>English content will be copied as a starting template for translation.</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={handleAddHelpLanguage} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Add</button>
                  <button onClick={() => { setShowHelpAddModal(false); setNewHelpLang({ code: '', name: '' }); }} className={`flex-1 px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300'}`}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Help Language buttons */}
          <div className="flex gap-2 flex-wrap">
            {helpLanguages.map((lang) => (
              <button key={lang.lang_code} onClick={() => setSelectedHelpLang(lang.lang_code)} className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                selectedHelpLang === lang.lang_code
                  ? 'bg-blue-600 text-white border-blue-600'
                  : isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 hover:bg-gray-50'
              } ${!lang.is_active ? 'opacity-50' : ''}`}>
                {lang.lang_code.toUpperCase()} — {lang.lang_name}
                {!lang.is_active && ' (off)'}
              </button>
            ))}
          </div>

          {/* Help Language actions */}
          {currentHelpLang && (
            <div className="flex gap-2 items-center flex-wrap">
              <button onClick={() => handleToggleHelp(currentHelpLang.lang_code)} className={`px-3 py-1.5 rounded text-sm text-white ${currentHelpLang.is_active ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}>
                {currentHelpLang.is_active ? 'Deactivate' : 'Activate'}
              </button>
              {currentHelpLang.lang_code !== 'en' && (
                <button onClick={() => handleDeleteHelp(currentHelpLang.lang_code)} className="px-3 py-1.5 rounded text-sm bg-red-600 text-white hover:bg-red-700">Delete</button>
              )}
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{allHelpKeys.length} keys</span>
            </div>
          )}

          {/* Search */}
          <input type="text" placeholder="Search keys or values..." value={searchHelpKey} onChange={(e) => setSearchHelpKey(e.target.value)} className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'border-gray-300 placeholder-gray-400'}`} />

          {/* Help Content table */}
          {currentHelpLang && (
            <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Key</th>
                      {selectedHelpLang !== 'en' && helpEnLang && <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English</th>}
                      <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{currentHelpLang.lang_name}</th>
                      <th className={`px-3 py-2 text-left font-medium w-20 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHelpKeys.map((key) => (
                      <tr key={key} className={`border-t ${isDarkMode ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`px-3 py-2 font-mono text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{key}</td>
                        {selectedHelpLang !== 'en' && helpEnLang && <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{helpEnLang.content[key] || '—'}</td>}
                        <td className="px-3 py-2">
                          {editingHelpKey === key ? (
                            <textarea value={editHelpValue} onChange={(e) => setEditHelpValue(e.target.value)} autoFocus rows={3}
                              className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                              onKeyDown={(e) => { if (e.key === 'Escape') setEditingHelpKey(null); }}
                            />
                          ) : (
                            <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'} whitespace-pre-wrap`}>{currentHelpLang.content[key] || ''}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {editingHelpKey === key ? (
                            <div className="flex gap-1 flex-col">
                              <button onClick={() => handleSaveHelpKey(currentHelpLang.lang_code, key, editHelpValue)} disabled={savingHelp} className="text-green-600 hover:text-green-700 text-xs font-medium">Save</button>
                              <button onClick={() => setEditingHelpKey(null)} className="text-gray-500 hover:text-gray-700 text-xs">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => { setEditingHelpKey(key); setEditHelpValue(currentHelpLang.content[key] || ''); }} className="text-blue-600 hover:text-blue-700 text-xs font-medium">Edit</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {helpLanguages.length === 0 && (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="text-lg mb-2">No Help content languages found</p>
              <p className="text-sm">Run the seed script first: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">python seed_help_content.py</code></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===============================
// HELP CONTENT TAB - Manage Help & About translations (kept for backwards compatibility)
// ===============================

export default LanguagesTabWithUI;
